import i18n from '@/$plugins/i18n/core'

/**
 * Detects if a product is from a B2B order or EndCustomer order
 * @param {Object} product - The product object
 * @param {Object} order - The order object (optional, for referenceDiscriminator check)
 * @returns {boolean} True if B2B order, false if EndCustomer order
 */
export function isB2BProduct (product, order = null) {
  // First check order referenceDiscriminator if available
  if (order && order.orderedBasket && order.orderedBasket.referenceDiscriminator) {
    return order.orderedBasket.referenceDiscriminator === 'UserModel'
  }

  // Fallback to product structure detection
  // B2B products have localizedName object or name string
  // EndCustomer products have name-{locale} properties
  if (product.localizedName && typeof product.localizedName === 'object') {
    return true
  }
  if (product.name && typeof product.name === 'string' && !product['name-de'] && !product['name-fr']) {
    return true
  }
  return false
}

/**
 * Gets the current locale in the format used by EndCustomer products (e.g., 'de', 'fr')
 * @returns {string} The two-letter language code
 */
function getCurrentLanguageCode () {
  const locale = i18n.locale || 'de-CH'
  return locale.split('-')[0].toLowerCase()
}

/**
 * Gets the full locale (e.g., 'de-CH', 'fr-CH')
 * @returns {string} The full locale string
 */
function getCurrentLocale () {
  return i18n.locale || 'de-CH'
}

/**
 * Extracts the product name, handling both B2B and EndCustomer formats
 * @param {Object} product - The product object
 * @param {Object} order - The order object (optional)
 * @returns {string} The product name
 */
export function getProductName (product, order = null) {
  if (!product) {
    return ''
  }

  const isB2B = isB2BProduct(product, order)

  if (isB2B) {
    // B2B format: product.name or product.localizedName[locale]
    if (product.localizedName && typeof product.localizedName === 'object') {
      const locale = getCurrentLocale()
      return product.localizedName[locale] || product.localizedName['de-CH'] || product.localizedName['fr-CH'] || product.name || ''
    }
    return product.name || ''
  } else {
    // EndCustomer format: product['name-{locale}']
    const langCode = getCurrentLanguageCode()
    const localizedName = product[`name-${langCode}`]
    if (localizedName) {
      return localizedName
    }
    // Fallback to other languages
    return product['name-de'] || product['name-fr'] || product['name-it'] || product['name-en'] || ''
  }
}

/**
 * Extracts the product ID or article number
 * @param {Object} product - The product object
 * @returns {string} The product ID or article number
 */
export function getProductId (product) {
  if (!product) {
    return ''
  }
  return product.articleNumber || product.id || ''
}

/**
 * Extracts the product image URL
 * @param {Object} product - The product object
 * @returns {string|null} The product image URL or null
 */
export function getProductImage (product) {
  if (!product) {
    return null
  }
  return product.image || null
}

/**
 * Extracts the product model (B2B only, may not exist in EndCustomer)
 * @param {Object} product - The product object
 * @returns {string} The product model or empty string
 */
export function getProductModel (product) {
  if (!product) {
    return ''
  }
  return product.model || ''
}

/**
 * Extracts the product warehouse (B2B only, may not exist in EndCustomer)
 * @param {Object} product - The product object
 * @returns {string|null} The product warehouse or null
 */
export function getProductWarehouse (product) {
  if (!product) {
    return null
  }
  return product.warehouse || null
}

/**
 * Extracts the product bundle count (B2B only, may not exist in EndCustomer)
 * @param {Object} product - The product object
 * @returns {number} The product bundle count or 0
 */
export function getProductBundleCount (product) {
  if (!product) {
    return 0
  }
  return product.bundleCount || 0
}

/**
 * Detects if an order is an EndCustomer order (vs B2B order)
 * @param {Object} order - The order object
 * @returns {boolean} True if EndCustomer order, false if B2B order
 */
export function isEndCustomerOrder (order) {
  if (!order || !order.configuration) {
    return false
  }
  // EndCustomer orders have a tenant property in configuration
  return !!order.configuration.tenant
}

